import type { Maybe } from '@adornis/base/utilTypes.js';
import { Int } from '@adornis/baseql/baseqlTypes.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { type BaseQLSelectionSet, selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { ExerciseType } from '@chancenwerk/enums/enums.js';
import { contentService } from '@chancenwerk/h5p/client/ContentService.js';
import { LevumiMathCategory } from '../../enums/CampusLevumiEnums.js';
import { ContentResultEventlog } from '../../eventlogs/ContentResultEventlog.js';
import { ScopedEntity } from '../../ScopedEntity.js';
import { LearningContentAPI } from '../api/LearningContentAPI.js';
import { CampusLevumiLevel } from './levumi/CampusLevumiLevel.js';

@Entity()
export class CampusExercise extends ScopedEntity {
  public static override _class = 'CampusExercise';
  public static override _collectionName = 'campus-exercises';

  static override get allFields() {
    return selectionSet(() => CampusExercise, 3, ['answers']);
  }

  static latexExpressionRegex = /\$(.*?)\$/g;

  @Field(() => Int, { default: v => v ?? ExerciseType.H5P })
  public type!: number;

  @Field(() => String)
  public levumiLevelID: Maybe<string>;

  @DataLoadedField(() => CampusLevumiLevel, ({ levumiLevelID }) => levumiLevelID)
  public levumiLevel!: CampusLevumiLevel;

  @Field(() => String, { default: v => v ?? LevumiMathCategory.ADD })
  public levumiMathCategory!: LevumiMathCategory;

  @Field(() => String)
  public questionText: Maybe<string>;

  @Field(() => String)
  public questionAnswer: Maybe<string>;

  @Field(() => String)
  public questionAnswerVariableFields: Maybe<string>;

  @Field(() => String)
  public h5pContentID: Maybe<string>;

  @Field(() => String)
  public h5pContentDesc: Maybe<string>;

  @Field(() => String)
  public h5pContentTitle: Maybe<string>;

  @Field(() => Int, { default: v => v ?? -1 })
  public minAnswerLength!: number;

  @Field(() => Boolean, { default: v => v ?? false })
  public isSubmittable!: boolean;

  @Field(() => String)
  public mediaID: Maybe<string>;

  @DataLoadedField(() => ImageFile, ({ mediaID }) => mediaID)
  public media!: ImageFile;

  @Field(() => Int, { default: v => v ?? 0 })
  public sortingOrderInLesson!: number;

  @Field(() => String)
  public title: Maybe<string>;

  @Field(() => String)
  public serloContentID: Maybe<string>;

  @Field(() => String, { default: v => v ?? '' })
  public serloEditorState: Maybe<string>;

  @Field(() => String)
  public chemistryFormula: Maybe<string>;

  @Field(() => GeogebraExercise, { default: v => v ?? new GeogebraExercise({}) })
  public geogebraContent!: GeogebraExercise;

  @Field(() => [ContentResultEventlog], {
    resolve: function (this: CampusExercise) {
      return async (gqlFields: BaseQLSelectionSet<ContentResultEventlog>) => this.answers;
    },
  })
  answers?: ContentResultEventlog[];

  public get typeString() {
    switch (this.type) {
      case ExerciseType.H5P:
        return 'H5P';
      case ExerciseType.FREITEXT:
        return 'Freitext';
      case ExerciseType.FORMEL:
        return 'Formel';
      case ExerciseType.GEOGEBRA:
        return 'GeoGebra';
      case ExerciseType.MEDIA:
        return 'Media-Aufgabe';
      case ExerciseType.LEVUMIGERMAN:
        return 'LEVUMI-Test Deutsch';
      case ExerciseType.LEVUMIMATH:
        return 'LEVUMI-Test Mathematik';
      case ExerciseType.SERLO:
        return 'Serlo';
      case ExerciseType.SERLO_EDITOR:
        return 'Serlo Editor';
      case ExerciseType.CHEMISTRY_FORMULA:
        return 'Chemie-Formel';
      default:
        return 'Unknown';
    }
  }

  public override async remove() {
    if (this.type === ExerciseType.H5P) {
      if (!this.h5pContentID) throw new Error('expecting h5pContentID on exercise of type h5p');
      await contentService.delete(this.h5pContentID);
    }
    if (this.type === ExerciseType.MEDIA && this.mediaID !== '') {
      await ImageFile.removeFile(this.mediaID ?? '')();
    }

    const lessonOfExercise = await LearningContentAPI.getLessonForExercise(this._id)({ exerciseIDs: 1 });
    if (!lessonOfExercise) throw new Error('Lesson of Exercise not found, this shouldnt happen');
    const indexOfRemovedExercise = lessonOfExercise.exerciseIDs.findIndex(lessonID => lessonID === this._id);
    lessonOfExercise.exerciseIDs.splice(indexOfRemovedExercise, 1);
    await lessonOfExercise.save();
    return super.remove();
  }
}
/**
 * @entity GeogebraExercise
 */
@Entity()
export class GeogebraExercise extends AdornisEntity {
  public static override _class = 'GeogebraExercise';

  @Field(() => String)
  public geogebraID!: string;

  @Field(() => String)
  public height!: string;

  @Field(() => String)
  public width!: string;
}
